import { z } from 'zod'
import { paginationQuerySchema } from './pagination'

export const sendFriendRequestSchema = z.object({
  addresseeId: z.string().uuid(),
})

export const friendshipSchema = z.object({
  id: z.string().uuid(),
  status: z.string(),
  createdAt: z.string().nullable().optional(),
  acceptedAt: z.string().nullable().optional(),
})

export const sendFriendRequestResponseSchema = z.object({
  friendship: friendshipSchema,
})

const profileMiniSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
})

export const incomingRequestItemSchema = z.object({
  id: z.string().uuid(),
  requester: profileMiniSchema,
  mutualFriendCount: z.number().int().nonnegative(),
  /** From someone the viewer does not know yet: shown apart, without a push. */
  quiet: z.boolean(),
  createdAt: z.string(),
})

export const outgoingRequestItemSchema = z.object({
  id: z.string().uuid(),
  addressee: profileMiniSchema,
  createdAt: z.string(),
})

function paginatedListSchema<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
    page: z.number().int(),
    pageSize: z.number().int(),
    hasNext: z.boolean(),
  })
}

export const incomingRequestsResponseSchema = paginatedListSchema(incomingRequestItemSchema)
export const outgoingRequestsResponseSchema = paginatedListSchema(outgoingRequestItemSchema)

export const acceptFriendRequestResponseSchema = z.object({
  friendship: z.object({
    id: z.string().uuid(),
    status: z.string(),
    acceptedAt: z.string().nullable(),
  }),
})

export const qrAddFriendResponseSchema = acceptFriendRequestResponseSchema.extend({
  friend: profileMiniSchema,
})

export const setBestFriendResponseSchema = z.object({
  /** True when the other side had already starred back — alerts are live now. */
  mutual: z.boolean(),
})

export const incomingBestFriendsResponseSchema = z.object({
  items: z.array(profileMiniSchema),
})

export type SetBestFriendResponse = z.infer<typeof setBestFriendResponseSchema>
export type IncomingBestFriendsResponse = z.infer<typeof incomingBestFriendsResponseSchema>

export const friendGroupMiniSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  color: z.string(),
})

export const friendListItemSchema = z.object({
  friendshipId: z.string().uuid(),
  friend: z.object({
    id: z.string().uuid(),
    displayName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    homeTown: z.string(),
  }),
  groups: z.array(friendGroupMiniSchema),
  acceptedAt: z.string(),
  /** Marked as best friend by the caller — subscribes them to arrival alerts. */
  isBestFriend: z.boolean(),
})

export const friendListQuerySchema = paginationQuerySchema.extend({
  q: z.string().optional(),
  groupId: z.string().uuid().optional(),
})

export const friendListResponseSchema = paginatedListSchema(friendListItemSchema)

export const blockedUserSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  blockedAt: z.string(),
})

export const blockedListResponseSchema = z.array(blockedUserSchema)

const friendGroupColorSchema = z.enum([
  'red',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
])

export const createFriendGroupRequestSchema = z.object({
  name: z.string().min(1).max(100),
  groupType: z.enum(['best_friends', 'close', 'friends', 'acquaintances', 'custom']).optional(),
  color: friendGroupColorSchema.optional(),
})

export const updateFriendGroupRequestSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  color: friendGroupColorSchema.optional(),
  sortOrder: z.number().int().min(0).optional(),
})

export const friendGroupSchema = z.object({
  id: z.string().uuid(),
  ownerId: z.string().uuid().optional(),
  name: z.string(),
  groupType: z.string().optional(),
  color: z.string(),
  sortOrder: z.number().int().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})

export const friendGroupWithCountSchema = friendGroupSchema.extend({
  memberCount: z.number().int().nonnegative(),
})

export const friendGroupResponseSchema = z.object({
  group: friendGroupSchema,
})

export const friendGroupListResponseSchema = z.array(friendGroupWithCountSchema)

export const addGroupMemberRequestSchema = z.object({
  friendshipId: z.string().uuid(),
})

export const userSearchItemSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  username: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  homeTown: z.string(),
  mutualFriendCount: z.number().int().nonnegative(),
})

export const userSearchQuerySchema = paginationQuerySchema.extend({
  q: z.string().min(2),
})

export const userSearchResponseSchema = paginatedListSchema(userSearchItemSchema)

export const friendSuggestionSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  mutualFriendCount: z.number().int().nonnegative(),
  mutualFriends: z.array(
    z.object({
      displayName: z.string(),
      avatarUrl: z.string().nullable(),
    }),
  ),
})

export const friendSuggestionsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(10),
})

export const friendSuggestionsResponseSchema = z.array(friendSuggestionSchema)

export const contactsMatchRequestSchema = z.object({
  phones: z.array(z.string()).max(500),
})

export const rateLimitResponseSchema = z.object({
  error: z.literal('rate_limit'),
  message: z.string(),
})

export type RateLimitResponse = z.infer<typeof rateLimitResponseSchema>

export const contactMatchSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  /** The number from the request that matched, so the app can tell which contact it was. */
  phone: z.string(),
})

export const contactsMatchResponseSchema = z.array(contactMatchSchema)

/** The own contact book: whether joins are announced and how many hashed numbers are stored. */
export const contactBookSettingsSchema = z.object({
  notifyContactJoins: z.boolean(),
  storedCount: z.number().int(),
})

export type ContactBookSettings = z.infer<typeof contactBookSettingsSchema>

/** Turning it off also deletes the stored contact hashes. */
export const updateContactBookSettingsRequestSchema = z.object({
  notifyContactJoins: z.boolean(),
})

export const createInviteLinkRequestSchema = z.object({
  maxUses: z.number().int().min(1).max(100).default(1),
  // 30d default: an invite link must survive the recipient's install round trip.
  expiresInHours: z.number().int().min(1).max(720).default(720),
  // When set, redeeming the link also credits the creator toward the drop's
  // `invite_friends` eligibility rule and lands the invitee on the drop.
  dropId: z.string().uuid().optional(),
  // A friend QR: one use within minutes, ignoring `maxUses` and `expiresInHours`.
  qr: z.boolean().optional(),
})

export const inviteLinkResponseSchema = z.object({
  code: z.string(),
  url: z.string(),
  maxUses: z.number().int(),
  expiresAt: z.string(),
  dropId: z.string().uuid().nullable(),
})

export type InviteLinkResponse = z.infer<typeof inviteLinkResponseSchema>

export const redeemInviteLinkResponseSchema = z.object({
  friendship: z.object({
    id: z.string().uuid(),
    status: z.string(),
  }),
  dropId: z.string().uuid().nullable(),
  friend: profileMiniSchema,
})

export type RedeemInviteLinkResponse = z.infer<typeof redeemInviteLinkResponseSchema>

export type FriendListItem = z.infer<typeof friendListItemSchema>
export type FriendGroupWithCount = z.infer<typeof friendGroupWithCountSchema>
export type UserSearchItem = z.infer<typeof userSearchItemSchema>
export type FriendSuggestion = z.infer<typeof friendSuggestionSchema>
export type CreateFriendGroupRequest = z.infer<typeof createFriendGroupRequestSchema>
export type UpdateFriendGroupRequest = z.infer<typeof updateFriendGroupRequestSchema>
